import { useTranslation } from 'react-i18next';
import { FolderSimple, UsersThree } from '@phosphor-icons/react';
import type { DashboardDocumentDto } from '@monorepo/shared';
import { DocumentStatus } from '@monorepo/shared';

interface RecentDocumentsTableProps {
  documents: DashboardDocumentDto[];
  isLoading: boolean;
  onOpenDocument: (nodeId: string) => void;
  onResendDocument: (documentId: string) => void;
}

export const RecentDocumentsTable = ({
  documents,
  isLoading,
  onOpenDocument,
  onResendDocument,
}: RecentDocumentsTableProps) => {
  const { t } = useTranslation();

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case DocumentStatus.DIFFUSED:
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-md bg-blue-100 text-blue-700">
            {t('dashboard.documents.status.diffused')}
          </span>
        );
      case DocumentStatus.VALIDATED:
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-md bg-green-100 text-green-700">
            {t('dashboard.documents.status.validated')}
          </span>
        );
      case DocumentStatus.PENDING_VALIDATION:
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-md bg-yellow-100 text-yellow-700">
            {t('dashboard.documents.status.pending_validation')}
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-md bg-gray-100 text-gray-700">
            {t('dashboard.documents.status.draft')}
          </span>
        );
    }
  };

  const getActionButton = (doc: DashboardDocumentDto) => {
    if (doc.status === DocumentStatus.DIFFUSED) {
      return (
        <button
          onClick={() => onResendDocument(doc.id)}
          className="px-4 py-1.5 text-sm font-medium text-white bg-primary rounded-md hover:bg-opacity-90 transition"
        >
          {t('dashboard.documents.actions.resend')}
        </button>
      );
    }
    return (
      <button
        onClick={() => onOpenDocument(doc.nodeId)}
        className="px-4 py-1.5 text-sm font-medium text-white bg-primary rounded-md hover:bg-opacity-90 transition"
      >
        {t('dashboard.documents.actions.open')}
      </button>
    );
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <div className="h-6 w-40 bg-gray-200 rounded animate-pulse" />
        </div>
        <div className="p-4 space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <div className="flex items-center gap-2 p-4 border-b border-gray-200">
        <FolderSimple size={20} weight="regular" className="text-primary" />
        <h2 className="text-lg font-semibold text-gray-900">{t('dashboard.documents.title')}</h2>
      </div>

      {documents.length === 0 ? (
        <div className="p-8 text-center text-gray-500">{t('dashboard.documents.empty')}</div>
      ) : (
        <div className="w-full overflow-x-auto">
          <table className="w-full min-w-[720px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('dashboard.documents.columns.name')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('dashboard.documents.columns.status')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('dashboard.documents.columns.update_date')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('dashboard.documents.columns.opened')}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('dashboard.documents.columns.action')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {documents.map((doc) => (
                <tr key={doc.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <span className="text-sm font-medium text-gray-900">{doc.name}</span>
                  </td>
                  <td className="px-4 py-3">{getStatusBadge(doc.status)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 text-sm text-gray-500">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      {formatDate(doc.updatedAt)}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {doc.totalRecipients > 0 ? (
                      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-700">
                        <UsersThree size={16} weight="regular" className="text-gray-400" />
                        {t('dashboard.documents.opened_ratio', {
                          opened: doc.totalOpened,
                          total: doc.totalRecipients,
                        })}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">{getActionButton(doc)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
